import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthError,
  AuthenticationResult,
  InteractionStatus,
} from '@azure/msal-browser';
import { BehaviorSubject, Observable, firstValueFrom, from, of } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { apiRequest, loginRequest } from '../config/msal.config';
import { PermissionNames } from '../models/permission-names.const';
import {
  LoginRequest,
  LoginResponse,
  PracticeArea,
  Role,
  User,
} from '../models/user.models';
import { UserStateService } from './user-state.service';
import { isStaleAuthCacheError } from '../utils/auth-error.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private http = inject(HttpClient);
  private userStateService = inject(UserStateService);

  private loginDisplay = false;
  private initializationPromise: Promise<void> | null = null;
  private _bootstrap$: Observable<LoginResponse | null> | null = null;

  // User state management
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private userRolesSubject = new BehaviorSubject<Role[]>([]);
  private userPracticeAreasSubject = new BehaviorSubject<PracticeArea[]>([]);

  public currentUser$ = this.currentUserSubject.asObservable();
  public userRoles$ = this.userRolesSubject.asObservable();
  public userPracticeAreas$ = this.userPracticeAreasSubject.asObservable();

  /**
   * Shared bootstrap observable. Initializes MSAL, consumes the MSAL redirect promise
   * exactly once, and calls the backend to hydrate user state. All subscribers share the
   * cached result via shareReplay so only one HTTP call is made per page load.
   */
  get bootstrap$(): Observable<LoginResponse | null> {
    if (!this._bootstrap$) {
      this._bootstrap$ = from(this.runBootstrap()).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this._bootstrap$;
  }

  /**
   * Ensure MSAL is initialized before making any calls
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.initializationPromise) {
      this.initializationPromise = this.initializeMSAL();
    }
    return this.initializationPromise;
  }

  /**
   * Initialize MSAL instance
   */
  private async initializeMSAL(): Promise<void> {
    try {
      await this.msalService.instance.initialize();

      // After initialization, check if there's an account in cache
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        // Set the first account as active
        this.msalService.instance.setActiveAccount(accounts[0]);
      }
    } catch (error) {
      console.error('MSAL initialization failed:', error);
      this.initializationPromise = null; // Reset so we can try again
      throw error;
    }
  }

  /**
   * Runs the one-time bootstrap sequence: initialize MSAL, consume the redirect promise,
   * then load user state from the backend. Called only via bootstrap$ (shareReplay-cached).
   */
  private async runBootstrap(): Promise<LoginResponse | null> {
    try {
      await this.ensureInitialized();

      // A stale-cache relic must not abort the bootstrap: it throws even when
      // a valid cached session exists. A genuine authentication failure is
      // rethrown — swallowing it would skip the fresh-login path and leave the
      // user on a login page that never says anything went wrong.
      let redirectResult: AuthenticationResult | null = null;
      try {
        redirectResult =
          await this.msalService.instance.handleRedirectPromise();
      } catch (error) {
        if (!isStaleAuthCacheError(error)) {
          throw error;
        }
        console.warn('Ignoring stale auth cache entry during bootstrap:', error);
      }

      if (redirectResult) {
        this.msalService.instance.setActiveAccount(redirectResult.account);
      }

      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        return null;
      }

      if (redirectResult) {
        // Fresh redirect login — create or update user record via POST /api/auth/login
        return await firstValueFrom(this.callLoginEndpoint());
      }

      // Returning user with cached MSAL session — prefer /me, fall back to /login
      try {
        return await firstValueFrom(this.getCurrentUser());
      } catch {
        return await firstValueFrom(this.callLoginEndpoint());
      }
    } catch (error) {
      // Authentication failures reach subscribers: LoginComponent's error
      // handler is the only place the user is told that signing in failed.
      // Everything else (a failing /me call, say) still degrades to "not
      // signed in" so the login page renders normally.
      if (error instanceof AuthError) {
        console.error('Authentication failed during bootstrap:', error);
        throw error;
      }
      console.error('Bootstrap error:', error);
      return null;
    }
  }

  /**
   * Login with popup, then call our API login endpoint.
   *
   * The popup lands on the /redirect bridge route (the configured redirectUri),
   * which relays the auth response back over BroadcastChannel — COOP on the
   * Entra login pages severs the window handle, so the opener cannot read the
   * popup directly.
   *
   * Pass overrideInteractionInProgress when the user is explicitly retrying
   * after abandoning or timing out a previous attempt: MSAL still holds that
   * interaction open, and a fresh popup would otherwise be refused with
   * interaction_in_progress. MSAL's guidance is to set this only in response
   * to a deliberate user action, never as an automatic retry.
   */
  loginPopup(options?: {
    overrideInteractionInProgress?: boolean;
  }): Observable<LoginResponse> {
    return from(this.ensureInitialized()).pipe(
      switchMap(() =>
        this.msalService.loginPopup({
          ...loginRequest,
          overrideInteractionInProgress:
            options?.overrideInteractionInProgress ?? false,
        }),
      ),
      switchMap((msalResult: AuthenticationResult) => {
        // Unlike the redirect flow, nothing else marks the new account active.
        // Without this, the next token acquisition finds no active account and
        // opens a second popup.
        this.msalService.instance.setActiveAccount(msalResult.account);
        return this.callLoginEndpoint();
      }),
      tap((loginResponse: LoginResponse) => {
        // bootstrap$ cached null when the page booted signed-out; components
        // that consult it after the in-app navigation (e.g. the dashboard's
        // auth check) must see this login, or they bounce back to /login.
        this._bootstrap$ = of(loginResponse);
      }),
    );
  }

  /**
   * Call the API login endpoint
   */
  callLoginEndpoint(): Observable<LoginResponse> {
    const loginRequest: LoginRequest = {}; // Empty since user info comes from JWT
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/api/auth/login`, loginRequest)
      .pipe(
        tap((loginResponse: LoginResponse) => {
          this.updateUserState(loginResponse);
        }),
      );
  }

  /**
   * Update local user state
   */
  private updateUserState(loginResponse: LoginResponse): void {
    this.userRolesSubject.next(loginResponse.roles);
    this.userPracticeAreasSubject.next(loginResponse.practiceAreas);
    this.currentUserSubject.next(loginResponse.user);

    // Also update the UserStateService for components that use it
    if (loginResponse.user) {
      this.userStateService.updateUserData(
        loginResponse.user.firstName + ' ' + loginResponse.user.lastName,
        loginResponse.user.email,
      );
    }
  }

  /**
   * Login with full-page redirect. Kept as the fallback for when the login
   * popup cannot be opened (e.g. blocked); bootstrap$ consumes the redirect
   * response on the way back in.
   *
   * Subscribing matters: msal-angular wraps the call as from(promise) with the
   * promise already running, so leaving it unsubscribed starts the navigation
   * but drops any failure — an unhandled rejection instead of something the
   * caller can react to. On success this normally never settles, because the
   * page unloads first.
   */
  async loginRedirect(): Promise<void> {
    await this.ensureInitialized();
    await firstValueFrom(this.msalService.loginRedirect(loginRequest));
  }

  /**
   * Sign out of this app. Clears MSAL's cached account/tokens and local user
   * state without calling the Entra end-session endpoint, so the user's
   * Microsoft session in this browser (Outlook, Teams, other apps) is left
   * alone — by design: this signs out of STB Staffing, not out of Microsoft.
   * No Microsoft logout page or popup is shown; the next sign-in shows the
   * account picker (loginRequest sets prompt: select_account) with the
   * still-signed-in account available as a one-click choice.
   *
   * A consequence worth knowing: because the browser's Microsoft session
   * survives, signing back in needs no credentials. On a shared machine,
   * signing out of this app is not the same as securing the account.
   *
   * Owns the page replacement as well as the cache clear, so no caller can be
   * left holding a signed-out session in a still-signed-in app (see
   * endLocalSession).
   */
  async logout(): Promise<void> {
    await this.ensureInitialized();
    try {
      await this.msalService.instance.clearCache();
    } finally {
      this.endLocalSession();
    }
  }

  /**
   * Drops the signed-in session held in memory and replaces the page with the
   * login screen.
   *
   * The two steps belong together. Clearing earlier — while the app is still
   * on screen waiting for an MSAL round trip — visibly strips the
   * permission-gated UI the user is looking at, because every section bound to
   * roles re-evaluates to "no access". Clearing later, or leaving it to the
   * caller, means a sign-out that returns with the app still rendered as the
   * signed-in user. Doing both here, at the moment the page goes away, avoids
   * each: the load is what actually guarantees nothing survives, and the
   * explicit clearing keeps the promise honest if a caller ever stays put.
   */
  private endLocalSession(): void {
    this.clearUserState();
    // Drop the cached bootstrap so a stale signed-in response cannot be
    // replayed to whatever subscribes next.
    this._bootstrap$ = null;
    this.replaceWithLoginPage();
  }

  /**
   * Replaces the current page with the login screen.
   *
   * Its own method so tests have a seam: Chrome does not allow spying on
   * window.location.assign, and a real navigation would end the test run.
   */
  protected replaceWithLoginPage(): void {
    window.location.assign('/login');
  }

  /**
   * Clears the user state this service publishes, and the copy the shared
   * UserStateService holds for components that read it directly.
   */
  private clearUserState(): void {
    this.currentUserSubject.next(null);
    this.userRolesSubject.next([]);
    this.userPracticeAreasSubject.next([]);
    this.userStateService.clearUserData();
  }

  /**
   * Get account info
   */
  getAccount(): any {
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Check if user is logged in (synchronous)
   */
  isLoggedIn(): boolean {
    const account = this.msalService.instance.getActiveAccount();
    const isLoggedIn = account !== null;
    return isLoggedIn;
  }

  /**
   * Check if user is logged in (async version for initialization)
   */
  async isLoggedInAsync(): Promise<boolean> {
    try {
      await this.ensureInitialized();
      return this.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      return false;
    }
  }

  /**
   * Get user name
   */
  getUserName(): string {
    const account = this.getAccount();
    return account ? account.name : '';
  }

  /**
   * Get user email
   */
  getUserEmail(): string {
    const account = this.getAccount();
    return account ? account.username : '';
  }

  /**
   * Get access token
   */
  async getAccessToken(): Promise<string | null> {
    try {
      await this.ensureInitialized();
      const response = await this.msalService.instance.acquireTokenSilent({
        ...apiRequest,
        account: this.getAccount(),
      });
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token silently:', error);
      // Try to acquire token interactively if silent acquisition fails
      try {
        const response =
          await this.msalService.instance.acquireTokenPopup(apiRequest);
        return response.accessToken;
      } catch (interactiveError) {
        console.error(
          'Failed to acquire token interactively:',
          interactiveError,
        );
        return null;
      }
    }
  }

  /**
   * Get interaction status
   */
  getInteractionStatus(): Observable<InteractionStatus> {
    return this.msalBroadcastService.inProgress$;
  }

  /**
   * Check if interaction is in progress
   */
  isInteractionInProgress(): Observable<boolean> {
    return this.getInteractionStatus().pipe(
      map((status) => status !== InteractionStatus.None),
    );
  }

  /**
   * Get current user data from API (for app initialization)
   */
  getCurrentUser(): Observable<LoginResponse> {
    return this.http
      .get<LoginResponse>(`${environment.apiUrl}/api/auth/me`)
      .pipe(
        tap((loginResponse: LoginResponse) => {
          this.updateUserState(loginResponse);
        }),
      );
  }

  /**
   * Get current user from local state
   */
  getCurrentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Whether the given employee id identifies the current user.
   *
   * Compared case-insensitively to mirror the API, which decides self-access
   * with StringComparison.OrdinalIgnoreCase (see RequestService.CanViewResourceNote).
   * The two ids come from different stores — the manually maintained
   * Users.EmployeeId column versus the employee-sync-backed records — so casing
   * can differ for the same person, and a strict comparison here would disagree
   * with the server about who owns a row.
   *
   * Call this at the point of use rather than caching the result: user state is
   * loaded asynchronously after bootstrap, so an early read can be null.
   */
  isCurrentEmployee(employeeId: string | null | undefined): boolean {
    const myEmployeeId = this.currentUserSubject.value?.employeeId;
    // Both guards matter: without them two absent ids would compare equal and
    // every row would read as the current user's.
    if (!employeeId || !myEmployeeId) return false;
    return employeeId.toLowerCase() === myEmployeeId.toLowerCase();
  }

  /**
   * Merge a partial update into the current user in local state.
   * Use this after API calls that change user properties without a full re-login.
   */
  patchCurrentUser(patch: Partial<User>): void {
    const current = this.currentUserSubject.value;
    if (current) {
      this.currentUserSubject.next({ ...current, ...patch });
    }
  }

  /**
   * Get current user roles from local state
   */
  getCurrentUserRoles(): Role[] {
    return this.userRolesSubject.value;
  }

  /**
   * Get current user practice areas from local state
   */
  getCurrentUserPracticeAreas(): PracticeArea[] {
    return this.userPracticeAreasSubject.value;
  }

  /**
   * Check if user has a specific role
   */
  hasRole(roleName: string): boolean {
    const roles = this.getCurrentUserRoles();
    return roles.some(
      (role) => role.name.toLowerCase() === roleName.toLowerCase(),
    );
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.hasRole('Admin') || this.hasRole('Super Admin');
  }

  /**
   * Check if user can edit capacity for other resources
   */
  canEditResourceCapacity(): boolean {
    return this.hasNamedPermission(PermissionNames.Resources.Capacity.Write);
  }

  /**
   * Check if user has a named permission by checking the role's permissions collection.
   */
  hasNamedPermission(permissionName: string): boolean {
    const roles = this.getCurrentUserRoles();
    return roles.some(
      (role) =>
        role.isActive &&
        (role.permissions?.some(
          (p) => p.name === permissionName && p.isActive,
        ) ??
          false),
    );
  }

  /**
   * Check if user satisfies an arbitrary role predicate.
   * Prefer hasNamedPermission() for permission checks; use this only for
   * role-level checks that are not covered by named permissions (e.g. role.name).
   */
  hasPermission(permissionCheck: (role: Role) => boolean): boolean {
    const roles = this.getCurrentUserRoles();
    return roles.some((role) => role.isActive && permissionCheck(role));
  }

  /**
   * Check if user can view feature flag management
   */
  canViewFeatureFlagManagement(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.FeatureFlags.Read);
  }

  /**
   * Check if user can view app settings management
   */
  canViewAppSettingsManagement(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.AppSettings.Read);
  }

  /**
   * Check if user can view permissions management
   */
  canViewPermissionsManagement(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.Permissions.Read);
  }

  /**
   * Check if user can view users management
   */
  canViewUsersManagement(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.UserManagement.Read);
  }

  /**
   * Check if user can view practice areas management
   */
  canViewPracticeAreasManagement(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.PracticeArea.Read);
  }

  /**
   * Check if user can manage (add/remove) practice area assignments
   */
  canManagePracticeAreas(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.PracticeArea.Write);
  }

  /**
   * Check if user can view secondary office assignments
   */
  canViewSecondaryOffices(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.SecondaryOffices.Read);
  }

  /**
   * Check if user can manage (add/remove) secondary office assignments
   */
  canManageSecondaryOffices(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.SecondaryOffices.Write);
  }

  /**
   * Check if user can view time off policy management
   */
  canViewTimeOffPolicies(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.TimeOffPolicy.Read);
  }

  /**
   * Check if user can manage time off policy management
   */
  canManageTimeOffPolicies(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.TimeOffPolicy.Write);
  }

  /**
   * Check if user can view per-office scheduled-notification configuration
   */
  canViewOfficeNotifications(): boolean {
    return this.hasNamedPermission(
      PermissionNames.Admin.OfficeNotifications.Read,
    );
  }

  /**
   * Check if user can manage per-office scheduled-notification configuration
   */
  canManageOfficeNotifications(): boolean {
    return this.hasNamedPermission(
      PermissionNames.Admin.OfficeNotifications.Write,
    );
  }

  /**
   * Check if user can view per-office, per-practice-area capacity settings
   */
  canViewOfficePracticeAreaSettings(): boolean {
    return this.hasNamedPermission(
      PermissionNames.Admin.OfficePracticeAreaSettings.Read,
    );
  }

  /**
   * Check if user can manage per-office, per-practice-area capacity settings
   */
  canManageOfficePracticeAreaSettings(): boolean {
    return this.hasNamedPermission(
      PermissionNames.Admin.OfficePracticeAreaSettings.Write,
    );
  }

  /**
   * Check if user can view resources
   */
  canViewResources(): boolean {
    return this.hasNamedPermission(PermissionNames.Resources.Read);
  }

  /**
   * Check if user can view client matters
   * TODO: Validate — currently checks Matters.Read. Confirm whether Clients.Read is also required.
   */
  canViewClientMatters(): boolean {
    return this.hasNamedPermission(PermissionNames.Matters.Read);
  }

  /**
   * Check if user can edit matters (notes, etc.)
   */
  canEditMatters(): boolean {
    return this.hasNamedPermission(PermissionNames.Matters.Write);
  }

  /**
   * Check if user can edit resources (notes, etc.)
   */
  canEditResources(): boolean {
    return this.hasNamedPermission(PermissionNames.Resources.Write);
  }

  /**
   * Check if user can edit another resource's profile attributes
   * (skills, jurisdictions, languages, interests, sub-practice areas).
   * Reuses Resources.Write, matching the API's resource tag endpoints.
   */
  canEditResourceAttributes(): boolean {
    return this.hasNamedPermission(PermissionNames.Resources.Write);
  }

  /**
   * Check if user can view requests
   */
  canViewRequests(): boolean {
    return this.hasNamedPermission(PermissionNames.Requests.Read);
  }

  /**
   * Check if user can create new requests
   */
  canCreateRequests(): boolean {
    return this.hasNamedPermission(PermissionNames.Requests.Create);
  }

  /**
   * Check if user can manage requests (add/remove assigned resources)
   */
  canManageRequests(): boolean {
    return this.hasNamedPermission(PermissionNames.Requests.Edit);
  }

  /**
   * Check if user can create direct assignments (bypass the Request workflow).
   */
  canCreateAssignments(): boolean {
    return this.hasNamedPermission(PermissionNames.Assignments.Create);
  }

  /**
   * Check if user can remove assignments (Request-linked or direct).
   */
  canDeleteAssignments(): boolean {
    return this.hasNamedPermission(PermissionNames.Assignments.Delete);
  }

  /**
   * Check if user can edit assignments (status, dates, type).
   */
  canEditAssignments(): boolean {
    return this.hasNamedPermission(PermissionNames.Assignments.Edit);
  }

  /**
   * Check if user can view manager assignment notes.
   */
  canReadManagerAssignmentNotes(): boolean {
    return this.hasNamedPermission(
      PermissionNames.Assignments.ManagerNotes.Read,
    );
  }

  /**
   * Check if user can add, change or clear manager assignment notes.
   *
   * Distinct from canEditAssignments (Assignments.Edit, which covers status/dates/type):
   * the API's manager-note PUT and DELETE require Assignments.ManagerNotes.Write, so
   * gating the note field on Edit hands an editable box to a user whose save would 403.
   */
  canWriteManagerAssignmentNotes(): boolean {
    return this.hasNamedPermission(
      PermissionNames.Assignments.ManagerNotes.Write,
    );
  }

  /**
   * Check if user can view other resources' own assignment notes.
   * Writing resource notes is ownership-based (own assignments only),
   * so no write permission exists.
   */
  canReadResourceAssignmentNotes(): boolean {
    return this.hasNamedPermission(
      PermissionNames.Assignments.ResourceNotes.Read,
    );
  }

  /**
   * Check if user can view logs
   */
  canViewLogs(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.SystemLogs.Read);
  }

  /**
   * Check if user can view audit history
   */
  canViewAuditHistory(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.AuditHistory.Read);
  }

  /**
   * Check if user can view submitted user feedback
   */
  canViewUserFeedback(): boolean {
    return this.hasNamedPermission(PermissionNames.Admin.UserFeedback.Read);
  }

  /**
   * Check if user can view time off for resources
   */
  canViewResourceTimeOff(): boolean {
    return this.hasNamedPermission(PermissionNames.Resources.TimeOff.Read);
  }

  /**
   * Check if user can manage time off for resources
   */
  canSetResourceTimeOff(): boolean {
    return this.hasNamedPermission(PermissionNames.Resources.TimeOff.Write);
  }

  /**
   * Check if user can edit resource-level time off policies (entitled days & carryover)
   */
  canEditResourceTimeOffPolicy(): boolean {
    return this.hasNamedPermission(
      PermissionNames.Resources.TimeOffPolicy.Write,
    );
  }

  /**
   * Check if user can approve or deny time off requests
   */
  canApproveTimeOff(): boolean {
    return this.hasNamedPermission(PermissionNames.Resources.TimeOff.Approval);
  }
}
